import { createLink } from "@tanstack/react-router";
import { forwardRef } from "react";
import Box from "@mui/material/Box";
import { Button, ButtonProps, Link as MUILink, styled } from "@mui/material";

export const FlexBox = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

export const FlexCentered = styled(FlexBox)(() => ({
  justifyContent: "center",
}));

export const FlexBetween = styled(FlexBox)(() => ({
  justifyContent: "space-between",
}));

export const DisabledOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  background: theme.palette.background.paper,
  height: "100%",
  width: "100%",
  left: 0,
  top: 0,
  zIndex: 9,
  opacity: 0.7,
}));

export const StyledAnchor = styled("a")(() => ({
  textDecoration: "none",
  color: "inherit",
}));

/**
 * Internal destinations. Built with `createLink` rather than `styled(Link)`,
 * which erases the router's generics and silently degrades `to`, `params` and
 * `search` to untyped reducers.
 *
 * Anywhere a plain anchor needs `StyledAnchor`'s reset, use this instead of
 * hand-writing `style={{ textDecoration: "none", color: "inherit" }}`.
 */
export const RouteLink = createLink(StyledAnchor);

/**
 * `Button` renders its root as `LinkComponent` once it has a destination, so
 * this is where the anchor `RouteButton` produces gets `StyledAnchor`'s reset.
 * Defaulted before the spread so a call site can still override it.
 */
const AnchorButton = forwardRef<HTMLButtonElement, ButtonProps>(
  function AnchorButton(props, ref) {
    return <Button LinkComponent={StyledAnchor} ref={ref} {...props} />;
  }
);

/** A MUI `Button` that navigates: renders an anchor, styled as `RouteLink` is. */
export const RouteButton = createLink(AnchorButton);

/**
 * A MUI `Link` that navigates, keeping MUI's `color` and `underline` defaults.
 *
 * The deleted `styled()` wrapper looked like it forced `color: inherit`, but
 * emotion appended MUI's declarations after it, so these always drew
 * `primary.main`. Adding `color="inherit"` here is the login/register
 * regression, not a restoration. Plain-text links are `RouteLink`.
 */
export const RouteMUILink = createLink(MUILink);
